/**
 * @module ProductTile
 * @description Product card component that handles variant swatch picking
 * and DOM patching for variant changes on collection/search pages.
 * @requires @fc/core
 * @requires @fc/variant-picker
 * @requires @fc/component
 * @requires @fc/utilities
 * @requires @fc/events
 * @requires @fc/dom-patch
 * @customElement product-tile
 * @customElement swatch-picker
 */
import { OverflowList } from '@fc/core';
import VariantPicker from '@fc/variant-picker';
import { FCElement } from '@fc/component';
import { isDesktopBreakpoint, mediaQueryLarge, preloadImage, requestYieldCallback } from '@fc/utilities';
import { FCEvents } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';

const PRODUCT_CARD_IMAGE_DECODE_TIMEOUT_MS = 220;
const CARD_ALTERNATE_IMAGE_IDLE_WARM_LIMIT = 2;
const CARD_ALTERNATE_IMAGE_IDLE_DELAY_MS = 800;
const CARD_ALTERNATE_IMAGE_IDLE_TIMEOUT_MS = 2500;
const CARD_GALLERY_ACTION_SELECTOR = '[data-card-gallery-action], .card-gallery__controls [data-action]';
const CARD_GALLERY_SELECT_SELECTOR = '.card-gallery__pagination .carousel-control[data-slide-index]';
const CARD_GALLERY_DEFERRED_CONTAINER_SELECTOR = '[data-card-gallery-deferred-container]';
const CARD_SWATCH_MEDIA_INPUT_SELECTOR = 'swatch-picker input[data-option-media-id][data-option-value-id]';
const CARD_GALLERY_HOVER_PREVIEW_ATTRIBUTE = 'data-card-gallery-hover-preview';
const CARD_GALLERY_INTERACTION_ACTIVE_CLASS = 'is-card-gallery-interaction-active';
const FINE_POINTER_HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const CARD_GALLERY_NON_STATIC_MODES = new Set(['carousel', 'hover-preview']);
const PRODUCT_TILE_INTERACTIVE_SELECTOR = [
  'button',
  'input',
  'label',
  'select',
  'summary',
  'textarea',
  '[contenteditable="plaintext-only"]',
  '[contenteditable="true"]',
  '[data-product-tile-interactive]',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
const CARD_GALLERY_DATA_ATTRIBUTES = [
  'data-primary-image-url',
  'data-primary-image-srcset',
  'data-primary-image-max-url',
  'data-primary-image-alt',
  'data-primary-image-width',
  'data-primary-image-height',
  'data-alt-image-url',
  'data-alt-image-srcset',
  'data-alt-image-max-url',
  'data-alt-image-alt',
  'data-alt-image-width',
  'data-alt-image-height',
  'data-image-sizes',
];
const CARD_GALLERY_SLIDE_STATE_ATTRIBUTES = ['data-media-id', 'data-media-type', 'data-media-aspect-ratio'];
const CARD_GALLERY_SLIDE_SELECTOR = '[data-card-gallery-slide]';
const MEDIA_MODE_ALTERNATE = 'alternate';
const MEDIA_MODE_PRIMARY = 'primary';
const CARD_ALTERNATE_IMAGE_SLOW_CONNECTIONS = new Set(['slow-2g', '2g']);
const warmedCardAlternateCandidates = new WeakMap();
let cardAlternateImageIdleWarmReservations = 0;
let cardAlternateImageObserver = null;

function imageCandidateFrom(root) {
  const img = root?.querySelector?.('img');
  if (!(img instanceof HTMLImageElement)) return null;

  return {
    sizes: img.getAttribute('sizes') || '',
    src: img.getAttribute('src') || img.currentSrc || img.src || '',
    srcset: img.getAttribute('srcset') || '',
  };
}

function warmImageCandidate(candidate, { decode = false, timeoutMs = 0, fetchPriority = '' } = {}) {
  if (!candidate?.src && !candidate?.srcset) return Promise.resolve();

  if (!candidate.srcset && !fetchPriority) {
    if (candidate.src) preloadImage(candidate.src);
    return Promise.resolve();
  }

  const img = new Image();
  if (fetchPriority) img.fetchPriority = fetchPriority;
  if (candidate.sizes) img.sizes = candidate.sizes;
  img.srcset = candidate.srcset;
  if (candidate.src) img.src = candidate.src;

  if (!decode || typeof img.decode !== 'function') return Promise.resolve();

  const decodePromise = img.decode().catch(() => {});
  if (!timeoutMs) return decodePromise;

  return Promise.race([decodePromise, new Promise((resolve) => window.setTimeout(resolve, timeoutMs))]);
}

function cardSlideImageCandidate(slide) {
  if (!(slide instanceof HTMLElement)) return null;

  return (
    imageCandidateFrom(slide) ?? {
      sizes: slide.dataset.imageSizes || '',
      src: slide.dataset.primaryImageUrl || '',
      srcset: slide.dataset.primaryImageSrcset || '',
    }
  );
}

function nextCardAlternateImageCandidate(root) {
  const gallery = root?.querySelector?.('[data-ref="cardGallery"]');
  if (!(gallery instanceof HTMLElement)) return null;
  if (!CARD_GALLERY_NON_STATIC_MODES.has(gallery.dataset.cardGalleryMode ?? '')) return null;

  const slides = Array.from(gallery.querySelectorAll(CARD_GALLERY_SLIDE_SELECTOR)).filter(
    (slide) => slide instanceof HTMLElement,
  );
  if (slides.length <= 1) return null;

  const activeSlide = gallery.querySelector(`${CARD_GALLERY_SLIDE_SELECTOR}.is-active`);
  const activeIndex = Math.max(0, slides.indexOf(activeSlide));
  const nextSlide = slides[(activeIndex + 1) % slides.length];
  if (!(nextSlide instanceof HTMLElement) || nextSlide === activeSlide) return null;

  return cardSlideImageCandidate(nextSlide);
}

function allowsCardAlternateImageWarm() {
  if (window.Shopify?.designMode === true) return false;

  const connection = navigator.connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !CARD_ALTERNATE_IMAGE_SLOW_CONNECTIONS.has(connection.effectiveType ?? '');
}

function warmNextCardAlternateImage(root) {
  if (!(root instanceof HTMLElement) || !allowsCardAlternateImageWarm()) return false;

  const candidate = nextCardAlternateImageCandidate(root);
  if (!candidate?.src && !candidate?.srcset) return false;

  const candidateKey = `${candidate.src}|${candidate.srcset}|${candidate.sizes}`;
  if (warmedCardAlternateCandidates.get(root) === candidateKey) return true;

  warmedCardAlternateCandidates.set(root, candidateKey);
  void warmImageCandidate(candidate, { fetchPriority: 'low' }).catch(() => {});
  return true;
}

function scheduleCardAlternateImageIdleWarm(callback) {
  const scheduleIdle = () => {
    window.setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(callback, { timeout: CARD_ALTERNATE_IMAGE_IDLE_TIMEOUT_MS });
      } else {
        callback();
      }
    }, CARD_ALTERNATE_IMAGE_IDLE_DELAY_MS);
  };

  if (document.readyState === 'complete') {
    scheduleIdle();
  } else {
    window.addEventListener('load', scheduleIdle, { once: true });
  }
}

function observeCardAlternateImageWarm(root) {
  if (!(root instanceof HTMLElement)) return;
  if (!allowsCardAlternateImageWarm()) return;
  if (!nextCardAlternateImageCandidate(root)) return;
  if (cardAlternateImageIdleWarmReservations >= CARD_ALTERNATE_IMAGE_IDLE_WARM_LIMIT) return;
  if (typeof IntersectionObserver !== 'function') return;

  if (!cardAlternateImageObserver) {
    cardAlternateImageObserver = new IntersectionObserver((entries, observer) => {
      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting)
        .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top);

      for (const entry of visibleEntries) {
        if (cardAlternateImageIdleWarmReservations >= CARD_ALTERNATE_IMAGE_IDLE_WARM_LIMIT) break;
        if (!(entry.target instanceof HTMLElement)) continue;

        observer.unobserve(entry.target);
        cardAlternateImageIdleWarmReservations += 1;
        scheduleCardAlternateImageIdleWarm(() => {
          if (entry.target.isConnected) warmNextCardAlternateImage(entry.target);
        });
      }

      if (cardAlternateImageIdleWarmReservations >= CARD_ALTERNATE_IMAGE_IDLE_WARM_LIMIT) {
        observer.disconnect();
        cardAlternateImageObserver = null;
      }
    });
  }

  cardAlternateImageObserver.observe(root);
}

function unobserveCardAlternateImageWarm(root) {
  if (!(root instanceof HTMLElement)) return;
  cardAlternateImageObserver?.unobserve(root);
}

function preloadCardImageFromDocument(doc) {
  const media = doc?.querySelector?.('.card-gallery__media-link, .product-media-container');
  void warmImageCandidate(imageCandidateFrom(media));
}

function promoteIncomingImage(root) {
  const img = root?.querySelector?.('img');
  if (!(img instanceof HTMLImageElement)) return;

  img.setAttribute('loading', 'eager');
  img.setAttribute('fetchpriority', 'high');
}

function setAttributeIfChanged(element, name, value) {
  if (value == null) return;
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

function removeAttributeIfPresent(element, name) {
  if (element.hasAttribute(name)) element.removeAttribute(name);
}

function numberFromDataset(value) {
  const number = Number.parseInt(value ?? '', 10);
  return Number.isFinite(number) ? number : null;
}

function supportsFinePointerHover() {
  return typeof window.matchMedia === 'function' && window.matchMedia(FINE_POINTER_HOVER_QUERY).matches;
}

function hydrateDeferredMediaSlide(slide) {
  if (!(slide instanceof HTMLElement)) return false;
  if (!slide.hasAttribute('data-card-gallery-deferred-slide')) return true;
  if (slide.querySelector('img')) {
    slide.classList.remove('card-gallery__media-slide--deferred');
    slide.removeAttribute('data-card-gallery-deferred-slide');
    return true;
  }

  const { dataset } = slide;
  if (!dataset.primaryImageUrl) return false;

  let container = slide.querySelector(CARD_GALLERY_DEFERRED_CONTAINER_SELECTOR);
  if (!(container instanceof HTMLElement)) {
    container = document.createElement('div');
    container.setAttribute('data-card-gallery-deferred-container', '');
    slide.append(container);
  }

  const mediaType = dataset.mediaType || 'image';
  container.className = `product-media-container media-fit product-media-container--${mediaType}`;
  if (slide.hasAttribute('data-card-gallery-constrain-height')) container.classList.add('constrain-height');

  const media = document.createElement('div');
  media.className = 'product-media';
  if (dataset.mediaId) media.dataset.mediaId = dataset.mediaId;
  if (dataset.mediaAspectRatio) media.style.setProperty('--ratio', dataset.mediaAspectRatio);

  const img = new Image();
  img.className = 'product-media__image';
  img.alt = dataset.primaryImageAlt || '';
  img.loading = 'eager';
  img.fetchPriority = 'high';
  img.setAttribute('fetchpriority', 'high');
  if (dataset.imageSizes) img.sizes = dataset.imageSizes;
  if (dataset.primaryImageSrcset) img.srcset = dataset.primaryImageSrcset;
  const width = numberFromDataset(dataset.primaryImageWidth);
  const height = numberFromDataset(dataset.primaryImageHeight);
  if (width) img.width = width;
  if (height) img.height = height;
  if (dataset.primaryImageMaxUrl) {
    img.setAttribute('data-max-resolution', dataset.primaryImageMaxUrl);
    img.setAttribute('data_max_resolution', dataset.primaryImageMaxUrl);
  }
  img.src = dataset.primaryImageUrl;

  media.append(img);
  container.replaceChildren(media);
  slide.classList.remove('card-gallery__media-slide--deferred');
  slide.removeAttribute('data-card-gallery-deferred-slide');
  return true;
}

function setImageSourceFromDataset(img, source, mode) {
  const dataset = source?.dataset;
  if (!dataset) return false;

  const prefix = mode === MEDIA_MODE_ALTERNATE && dataset.altImageUrl ? 'alt' : 'primary';
  const url = dataset[`${prefix}ImageUrl`];
  if (!url) return false;

  const srcset = dataset[`${prefix}ImageSrcset`];
  const sizes = dataset.imageSizes;
  const alt = dataset[`${prefix}ImageAlt`];
  const width = dataset[`${prefix}ImageWidth`];
  const height = dataset[`${prefix}ImageHeight`];
  const maxResolution = dataset[`${prefix}ImageMaxUrl`] || url;

  if (sizes) setAttributeIfChanged(img, 'sizes', sizes);
  if (srcset) {
    setAttributeIfChanged(img, 'srcset', srcset);
  } else {
    removeAttributeIfPresent(img, 'srcset');
    if (img.srcset !== '') img.srcset = '';
  }
  if (alt !== undefined) setAttributeIfChanged(img, 'alt', alt);
  if (width) setAttributeIfChanged(img, 'width', width);
  if (height) setAttributeIfChanged(img, 'height', height);
  if (img.getAttribute('src') !== url) {
    img.setAttribute('src', url);
    img.src = url;
  }

  setAttributeIfChanged(img, 'data-max-resolution', maxResolution);
  setAttributeIfChanged(img, 'data_max_resolution', maxResolution);
  return true;
}

export class ProductItem extends FCElement {
  requiredRefs = [];
  #statusAnnouncementFrame = null;
  #imageSwapVersion = 0;
  #variantPickVersion = 0;
  #cardGalleryInteractionTarget = null;
  #cardGalleryPointerFocusTarget = null;
  #hoverPreviewMediaId = '';

  get productLink() {
    return this.refs.productItemLink ?? null;
  }

  get statusRegion() {
    const region = this.querySelector('[data-ref="productItemStatus"]');
    return region instanceof HTMLElement ? region : null;
  }

  getSelectedVariantId() {
    const input = this.querySelector('input[type="radio"]:checked[data-variant-id]');
    return input instanceof HTMLInputElement ? (input.dataset.variantId ?? null) : null;
  }

  getProductItemLink() {
    return this.productLink;
  }

  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  get variantPicker() {
    return this.querySelector('swatch-picker');
  }

  #prefetchHandler = () => {
    this.variantPicker?.prefetchVariantSections?.();
  };

  #onCardAlternateImageWarmIntent = (event) => {
    if (
      event.type === 'pointerenter' &&
      typeof PointerEvent !== 'undefined' &&
      event instanceof PointerEvent &&
      event.pointerType !== 'mouse'
    ) {
      return;
    }

    warmNextCardAlternateImage(this);
  };

  #goToURL = (event, url) => {
    if (!url || !(url instanceof URL)) return;

    const openNew =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (openNew) {
      event.preventDefault();
      window.open(url.href, '_blank');
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#setupQuickAdd();

    this.addEventListener(FCEvents.variantChange, this.#onVariantUpdate);
    this.addEventListener(FCEvents.variantPick, this.#onVariantSelected);
    mediaQueryLarge?.addEventListener('change', this.#setupQuickAdd);
    this.addEventListener('change', this.#onCardSwatchMediaChange);
    this.addEventListener('click', this.#onCardGalleryControlClick, { capture: true });
    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('pointerenter', this.#onCardAlternateImageWarmIntent);
    this.addEventListener('focusin', this.#onCardAlternateImageWarmIntent);
    this.#setupCardGalleryInteractions();
    observeCardAlternateImageWarm(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownCardGalleryInteractions();
    unobserveCardAlternateImageWarm(this);
    this.removeEventListener('click', this.#onCardGalleryControlClick, { capture: true });
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerenter', this.#onCardAlternateImageWarmIntent);
    this.removeEventListener('focusin', this.#onCardAlternateImageWarmIntent);
    this.removeEventListener('change', this.#onCardSwatchMediaChange);
    this.removeEventListener(FCEvents.variantChange, this.#onVariantUpdate);
    this.removeEventListener(FCEvents.variantPick, this.#onVariantSelected);
    this.removeEventListener('pointerenter', this.#prefetchHandler);
    mediaQueryLarge?.removeEventListener('change', this.#setupQuickAdd);
  }

  #setupQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#prefetchHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#prefetchHandler);
    }
  };

  #setupCardGalleryInteractions() {
    this.#teardownCardGalleryInteractions();

    const gallery = this.#cardGallery;
    if (!(gallery instanceof HTMLElement)) return;

    const needsHoverPreview = gallery.hasAttribute(CARD_GALLERY_HOVER_PREVIEW_ATTRIBUTE);
    const needsHoverControls = gallery.dataset.cardGalleryControlsVisibility === 'hover';
    if (!needsHoverPreview && !needsHoverControls) return;

    this.#cardGalleryInteractionTarget = gallery;
    gallery.addEventListener('pointerenter', this.#onCardGalleryPointerEnter);
    gallery.addEventListener('pointerleave', this.#onCardGalleryPointerLeave);
    gallery.addEventListener('pointerdown', this.#onCardGalleryPointerDown, { capture: true });
    gallery.addEventListener('focusin', this.#onCardGalleryFocusIn);
    gallery.addEventListener('focusout', this.#onCardGalleryFocusOut);
  }

  #teardownCardGalleryInteractions() {
    const gallery = this.#cardGalleryInteractionTarget;
    if (!(gallery instanceof HTMLElement)) return;

    gallery.removeEventListener('pointerenter', this.#onCardGalleryPointerEnter);
    gallery.removeEventListener('pointerleave', this.#onCardGalleryPointerLeave);
    gallery.removeEventListener('pointerdown', this.#onCardGalleryPointerDown, { capture: true });
    gallery.removeEventListener('focusin', this.#onCardGalleryFocusIn);
    gallery.removeEventListener('focusout', this.#onCardGalleryFocusOut);
    gallery.classList.remove(CARD_GALLERY_INTERACTION_ACTIVE_CLASS);
    this.#cardGalleryInteractionTarget = null;
    this.#cardGalleryPointerFocusTarget = null;
    this.#hoverPreviewMediaId = '';
  }

  #onCardGalleryPointerEnter = (event) => {
    if (!this.#isMousePointerEvent(event)) return;

    this.#setCardGalleryInteractionActive(true);
    this.#previewHoverMedia();
  };

  #onCardGalleryPointerLeave = (event) => {
    if (!this.#isMousePointerEvent(event)) return;

    this.#resetHoverMediaPreview();
    this.#releasePointerGalleryFocus();
    if (!this.#cardGallery?.contains(document.activeElement)) this.#setCardGalleryInteractionActive(false);
  };

  #onCardGalleryPointerDown = (event) => {
    if (!this.#isMousePointerEvent(event)) return;

    const eventTarget = event.target instanceof Element ? event.target : null;
    const controlTarget = eventTarget?.closest(`${CARD_GALLERY_ACTION_SELECTOR}, ${CARD_GALLERY_SELECT_SELECTOR}`);
    this.#cardGalleryPointerFocusTarget =
      controlTarget instanceof HTMLElement && this.#cardGallery?.contains(controlTarget) ? controlTarget : null;
  };

  #onCardGalleryFocusIn = (event) => {
    const focusTarget = event.target instanceof HTMLElement ? event.target : null;
    if (
      this.#cardGalleryPointerFocusTarget &&
      focusTarget instanceof HTMLElement &&
      focusTarget !== this.#cardGalleryPointerFocusTarget
    ) {
      this.#cardGalleryPointerFocusTarget = null;
    }

    this.#setCardGalleryInteractionActive(true);
  };

  #onCardGalleryFocusOut = (event) => {
    const gallery = this.#cardGallery;
    const relatedTarget =
      typeof FocusEvent !== 'undefined' && event instanceof FocusEvent && event.relatedTarget instanceof Node
        ? event.relatedTarget
        : null;
    if (gallery instanceof HTMLElement && relatedTarget && gallery.contains(relatedTarget)) return;

    this.#cardGalleryPointerFocusTarget = null;
    this.#setCardGalleryInteractionActive(false);
  };

  #releasePointerGalleryFocus() {
    const gallery = this.#cardGallery;
    const activeElement = document.activeElement;
    const pointerFocusTarget = this.#cardGalleryPointerFocusTarget;

    if (
      !(gallery instanceof HTMLElement) ||
      !(activeElement instanceof HTMLElement) ||
      !(pointerFocusTarget instanceof HTMLElement)
    ) {
      this.#cardGalleryPointerFocusTarget = null;
      return;
    }

    if (!gallery.contains(activeElement) || activeElement !== pointerFocusTarget) return;

    this.#cardGalleryPointerFocusTarget = null;
    activeElement.blur();
  }

  #isMousePointerEvent(event) {
    return typeof PointerEvent !== 'undefined' && event instanceof PointerEvent && event.pointerType === 'mouse';
  }

  #setCardGalleryInteractionActive(active) {
    const gallery = this.#cardGallery;
    if (!(gallery instanceof HTMLElement)) return;

    gallery.classList.toggle(CARD_GALLERY_INTERACTION_ACTIVE_CLASS, active);
  }

  #previewHoverMedia() {
    const gallery = this.#cardGallery;
    if (!(gallery instanceof HTMLElement)) return false;
    if (!gallery.hasAttribute(CARD_GALLERY_HOVER_PREVIEW_ATTRIBUTE)) return false;
    if (!supportsFinePointerHover()) return false;

    const slides = this.#mediaSlides();
    if (slides.length <= 1) return false;

    const activeSlide = this.#activeMediaSlide();
    this.#hoverPreviewMediaId = activeSlide?.dataset.mediaId ?? '';
    return this.#selectRelativeMediaSlide(1, { manual: false });
  }

  #resetHoverMediaPreview() {
    const mediaId = this.#hoverPreviewMediaId;
    this.#hoverPreviewMediaId = '';

    if (!mediaId) return false;
    return this.#selectMediaSlideById(mediaId, { commit: false, manual: false });
  }

  #onVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id ?? '';
    this.#variantPickVersion += 1;
    this.#imageSwapVersion += 1;

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
      return;
    }

    const selectedLocally = this.#selectVariantMediaByOptionValueId(optionValueId, { commit: true });
    if (!selectedLocally) this.#syncWarmedVariantMedia(optionValueId, this.#variantPickVersion);
  };

  #onCardSwatchMediaChange = (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (!input || !input.matches(CARD_SWATCH_MEDIA_INPUT_SELECTOR)) return;

    const picker = input.closest('swatch-picker');
    if (!(picker instanceof HTMLElement) || picker.closest('product-tile') !== this) return;

    const mediaId = input.dataset.optionMediaId ?? '';
    if (!mediaId) return;

    this.#selectMediaSlideById(mediaId, { commit: true, manual: false });
  };

  #lastVariantHtml = null;

  #onVariantUpdate = (event) => {
    event.stopPropagation();
    this.#lastVariantHtml = event.detail?.data?.html ?? null;
    const announcement = this.updatePrice(event);
    this.#checkUnavailable(event);

    if (event.target !== this.variantPicker) {
      const html = this.#lastVariantHtml;
      if (html) this.variantPicker?.updateVariantPicker?.(html);
    }

    void this.#syncVariantImages();
    this.#syncProductUrl(event);
    this.removeAttribute('data-no-swatch-selected');
    this.#reflowOverflow();
    this.#announceVariantState(announcement || this.#buildVariantAnnouncement(event.detail?.resource));
  };

  #reflowOverflow() {
    const ol = this.querySelector('swatch-picker overflow-row');
    if (!ol?.querySelector('[slot="overflow"]')) return;

    requestAnimationFrame(() => {
      ol.dispatchEvent(new CustomEvent('reflow', { bubbles: true, detail: {} }));
    });
  }

  updatePrice(event) {
    const currentPriceContainers = this.querySelectorAll('price-display [data-ref="price-container"]');
    const current = currentPriceContainers[currentPriceContainers.length - 1] ?? null;
    const incoming = event.detail.data.html.querySelector('price-display [data-ref="price-container"]');
    if (incoming && current) patchDOM(current, incoming);
    return this.#readPriceAnnouncement();
  }

  #readPriceAnnouncement() {
    const announcements = this.querySelectorAll('price-display [data-ref="priceAnnouncement"]');
    const announcement = announcements[announcements.length - 1] ?? null;
    return announcement instanceof HTMLElement ? announcement.textContent?.trim() || '' : '';
  }

  #buildVariantAnnouncement(variant) {
    const parts = [];
    const productTitle = this.dataset.productTitle?.trim();
    const variantTitle = typeof variant?.title === 'string' ? variant.title.trim() : '';

    if (productTitle) parts.push(productTitle);
    if (variantTitle && variantTitle !== 'Default Title') parts.push(variantTitle);

    if (variant?.available === true) {
      parts.push(this.dataset.availableLabel?.trim() || 'Available');
    } else if (variant?.available === false) {
      parts.push(this.dataset.unavailableLabel?.trim() || 'Unavailable');
    }

    return parts.join('. ');
  }

  #announceStatusMessage(message) {
    const region = this.statusRegion;
    const nextMessage = message?.trim();
    if (!region || !nextMessage) return;

    if (this.#statusAnnouncementFrame) {
      cancelAnimationFrame(this.#statusAnnouncementFrame);
    }

    region.textContent = '';
    this.#statusAnnouncementFrame = requestAnimationFrame(() => {
      this.#statusAnnouncementFrame = null;
      if (this.isConnected) {
        region.textContent = nextMessage;
      }
    });
  }

  #announceVariantState(message) {
    this.#announceStatusMessage(message);
  }

  #announceCarouselState(activeSlide) {
    const slides = this.#mediaSlides();
    if (slides.length <= 1 || !(activeSlide instanceof HTMLElement)) return;

    const activeIndex = Math.max(0, slides.indexOf(activeSlide));
    const template = this.dataset.productMediaStatusTemplate || 'Image [index] of [length] for [product]';
    const message = template
      .replace('[index]', `${activeIndex + 1}`)
      .replace('[length]', `${slides.length}`)
      .replace('[product]', this.dataset.productTitle?.trim() || '');

    this.#announceStatusMessage(message);
  }

  #syncProductUrl(event) {
    const anchor = event.detail.data.html?.querySelector('[data-ref="productItemLink"], [data-ref="productCardLink"]');
    const responseCard = event.detail.data.html?.querySelector('product-card');
    const mediaUrl = event.detail.data.html?.querySelector('card-link')?.getAttribute('data-featured-media-url');

    if (mediaUrl) {
      this.closest('card-link')?.setAttribute('data-featured-media-url', mediaUrl);
    }

    let resolvedUrl = null;
    let label = '';
    let productTitle = '';

    if (anchor instanceof HTMLAnchorElement) {
      if (anchor.getAttribute('href')?.trim() === '') return;
      resolvedUrl = new URL(anchor.href, window.location.origin);
      label = anchor.getAttribute('aria-label')?.trim() || '';
      productTitle = anchor.dataset.productTitle?.trim() || '';
    } else if (responseCard instanceof HTMLElement) {
      const responseHref = responseCard.dataset.productCardUrl?.trim() || '';
      if (!responseHref) return;
      resolvedUrl = new URL(responseHref, window.location.origin);
      label = responseCard.dataset.productCardLabel?.trim() || '';
      productTitle = responseCard.dataset.productTitle?.trim() || '';
    } else {
      return;
    }

    const selectedUrl = this.variantPicker?.buildLinkedProductUrl?.();
    if (selectedUrl instanceof URL) {
      resolvedUrl = selectedUrl;
    }
    if (!(resolvedUrl instanceof URL)) return;

    const { productItemLink, productTitleLink, cardGalleryLink } = this.refs;
    const href = resolvedUrl.href;

    if (productItemLink) productItemLink.href = href;
    if (productItemLink && label) productItemLink.setAttribute('aria-label', label);
    if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = href;
    if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = href;
    if (productTitle) this.dataset.productTitle = productTitle;
  }

  #checkUnavailable(event) {
    for (const v of event.detail.data.html.querySelectorAll('input:checked')) {
      this.#setCartBtn(v.dataset.optionAvailable === 'true');
    }
  }

  #setCartBtn(enabled) {
    const btn = this.querySelector('.add-to-cart__button button');
    if (btn instanceof HTMLButtonElement) btn.disabled = !enabled;
  }

  get #cardGallery() {
    return this.querySelector('[data-ref="cardGallery"]');
  }

  get #cardGalleryLink() {
    return this.#cardGallery?.querySelector('[data-ref="cardGalleryLink"]') ?? null;
  }

  get #mediaMode() {
    const link = this.#cardGalleryLink;
    return link?.dataset.mediaMode === MEDIA_MODE_ALTERNATE ? MEDIA_MODE_ALTERNATE : MEDIA_MODE_PRIMARY;
  }

  setMediaMode(mode) {
    const nextMode = mode === MEDIA_MODE_ALTERNATE ? MEDIA_MODE_ALTERNATE : MEDIA_MODE_PRIMARY;
    const gallery = this.#cardGallery;
    const link = this.#cardGalleryLink;
    if (!gallery || !(link instanceof HTMLElement)) return false;

    gallery.dataset.mediaMode = nextMode;
    link.dataset.mediaMode = nextMode;

    if (link.dataset.manualSlide === 'true') {
      this.#selectCommittedMediaSlide();
      link.dataset.manualSlide = 'false';
      return true;
    }

    const activeSlide = this.#activeMediaSlide();
    if (activeSlide) {
      this.#applyMediaModeToSlide(activeSlide);
      return true;
    }

    const image = link.querySelector('img');
    return image instanceof HTMLImageElement ? setImageSourceFromDataset(image, link, nextMode) : false;
  }

  previewVariantMedia(mediaId) {
    return this.#selectMediaSlideById(mediaId, { commit: false, manual: false });
  }

  resetVariantMediaPreview() {
    return this.#selectCommittedMediaSlide();
  }

  selectVariantMedia(mediaId) {
    return this.#selectMediaSlideById(mediaId, { commit: true, manual: false });
  }

  #onCardGalleryControlClick = (event) => {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const actionTarget = eventTarget?.closest(CARD_GALLERY_ACTION_SELECTOR);
    const selectTarget = eventTarget?.closest(CARD_GALLERY_SELECT_SELECTOR);

    if (this.#claimCardGalleryAction(event, actionTarget)) return;

    if (selectTarget instanceof HTMLElement && this.contains(selectTarget)) {
      const index = Number.parseInt(selectTarget.dataset.slideIndex ?? '', 10);
      if (!Number.isFinite(index)) return;

      event.preventDefault();
      event.stopPropagation();
      this.#selectMediaSlideByIndex(index, { manual: true });
    }
  };

  #claimCardGalleryAction(event, actionTarget) {
    if (!(actionTarget instanceof HTMLElement) || !this.contains(actionTarget)) return false;

    const action = actionTarget.dataset.cardGalleryAction || actionTarget.dataset.action;
    const selected = this.#selectRelativeMediaSlide(action === 'previous' ? -1 : 1);
    if (!selected) return false;

    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  #mediaSlides() {
    return Array.from(this.#cardGallery?.querySelectorAll(CARD_GALLERY_SLIDE_SELECTOR) ?? []).filter(
      (slide) => slide instanceof HTMLElement,
    );
  }

  #activeMediaSlide() {
    return this.#cardGallery?.querySelector(`${CARD_GALLERY_SLIDE_SELECTOR}.is-active`) ?? this.#mediaSlides()[0] ?? null;
  }

  #slideByMediaId(mediaId) {
    if (!mediaId) return null;
    return this.#mediaSlides().find((slide) => slide.dataset.mediaId === String(mediaId)) ?? null;
  }

  #selectVariantMediaByOptionValueId(optionValueId, { commit = true } = {}) {
    const mediaId = this.variantPicker?.getOptionMediaId?.(optionValueId) ?? '';
    return this.#selectMediaSlideById(mediaId, { commit, manual: false });
  }

  #selectMediaSlideById(mediaId, { commit = false, manual = false } = {}) {
    const slide = this.#slideByMediaId(mediaId);
    if (!slide) return false;

    return this.#selectMediaSlide(slide, { commit, manual });
  }

  #selectCommittedMediaSlide() {
    const link = this.#cardGalleryLink;
    const selectedMediaId =
      link?.dataset.selectedMediaId ||
      this.variantPicker?.selectedOption?.dataset.optionMediaId ||
      link?.dataset.activeMediaId ||
      this.#activeMediaSlide()?.dataset.mediaId ||
      '';

    return this.#selectMediaSlideById(selectedMediaId, { commit: false, manual: false });
  }

  #selectRelativeMediaSlide(delta, { commit = false, manual = true } = {}) {
    const slides = this.#mediaSlides();
    if (slides.length <= 1) return false;

    const current = this.#activeMediaSlide();
    const currentIndex = Math.max(0, slides.indexOf(current));
    const nextIndex = (currentIndex + delta + slides.length) % slides.length;
    return this.#selectMediaSlide(slides[nextIndex], { commit, manual });
  }

  #selectMediaSlideByIndex(index, { manual = false } = {}) {
    const slides = this.#mediaSlides();
    const slide = slides[index];
    if (!(slide instanceof HTMLElement)) return false;

    return this.#selectMediaSlide(slide, { commit: false, manual });
  }

  #selectMediaSlide(slide, { commit = false, manual = false } = {}) {
    if (!(slide instanceof HTMLElement)) return false;

    const link = this.#cardGalleryLink;
    if (!(link instanceof HTMLElement)) return false;
    hydrateDeferredMediaSlide(slide);

    for (const candidate of this.#mediaSlides()) {
      const active = candidate === slide;
      candidate.classList.toggle('is-active', active);
      candidate.setAttribute('aria-hidden', active ? 'false' : 'true');
    }

    link.dataset.activeMediaId = slide.dataset.mediaId ?? '';
    link.dataset.manualSlide = manual ? 'true' : 'false';
    if (commit) {
      link.dataset.selectedMediaId = slide.dataset.mediaId ?? '';
      this.#hoverPreviewMediaId = '';
    }
    this.#applyMediaModeToSlide(slide);
    this.#syncGalleryPagination(slide);
    if (manual) this.#announceCarouselState(slide);
    return true;
  }

  #syncGalleryPagination(activeSlide = this.#activeMediaSlide()) {
    const gallery = this.#cardGallery;
    if (!gallery || !(activeSlide instanceof HTMLElement)) return;

    const slides = this.#mediaSlides();
    const activeIndex = Math.max(0, slides.indexOf(activeSlide));
    const itemCount = slides.length || 1;

    for (const control of gallery.querySelectorAll('.card-gallery__pagination [data-slide-index]')) {
      if (!(control instanceof HTMLElement)) continue;
      control.setAttribute('aria-current', control.dataset.slideIndex === String(activeIndex) ? 'true' : 'false');
    }

    for (const current of gallery.querySelectorAll('.card-gallery__pagination [data-ref="current"]')) {
      current.textContent = `${activeIndex + 1}`;
    }

    for (const progressFill of gallery.querySelectorAll('.card-gallery__pagination [data-ref="progressFill"]')) {
      if (progressFill instanceof HTMLElement) {
        progressFill.style.width = `${((activeIndex + 1) / itemCount) * 100}%`;
      }
    }

    for (const progress of gallery.querySelectorAll('.card-gallery__pagination [role="progressbar"]')) {
      progress.setAttribute('aria-valuenow', `${activeIndex + 1}`);
    }
  }

  #incomingActiveMediaSlide(incomingLink) {
    if (!(incomingLink instanceof HTMLElement)) return null;

    return (
      incomingLink.querySelector(`${CARD_GALLERY_SLIDE_SELECTOR}.is-active`) ??
      incomingLink.querySelector(CARD_GALLERY_SLIDE_SELECTOR) ??
      null
    );
  }

  #incomingActiveMediaId(incomingLink) {
    if (!(incomingLink instanceof HTMLElement)) return '';

    const incomingActiveSlide = this.#incomingActiveMediaSlide(incomingLink);
    return incomingLink.dataset.activeMediaId || incomingActiveSlide?.dataset.mediaId || '';
  }

  #shouldPreserveLiveGalleryShell(currentLink, incomingLink, gallery) {
    if (!(currentLink instanceof HTMLElement) || !(incomingLink instanceof HTMLElement)) return false;
    if (!(gallery instanceof HTMLElement)) return false;
    if (!CARD_GALLERY_NON_STATIC_MODES.has(gallery.dataset.cardGalleryMode ?? '')) return false;

    const currentSlideCount = Number.parseInt(currentLink.dataset.cardGallerySlideCount ?? '', 10);
    const incomingSlideCount = Number.parseInt(incomingLink.dataset.cardGallerySlideCount ?? '', 10);
    if (!Number.isFinite(currentSlideCount) || currentSlideCount <= 1) return false;
    if (!Number.isFinite(incomingSlideCount) || incomingSlideCount >= currentSlideCount) return false;

    return this.#incomingActiveMediaSlide(incomingLink) instanceof HTMLElement;
  }

  #canPreserveActiveMediaPatch(currentLink, incomingLink, gallery) {
    if (!(currentLink instanceof HTMLElement) || !(incomingLink instanceof HTMLElement)) return false;
    if (!(gallery instanceof HTMLElement)) return false;

    const incomingGallery = incomingLink.closest('[data-ref="cardGallery"]');
    if (incomingGallery instanceof HTMLElement) {
      const currentProductId = gallery.dataset.productId ?? '';
      const incomingProductId = incomingGallery.dataset.productId ?? '';
      if (currentProductId && incomingProductId && currentProductId !== incomingProductId) return false;
    }

    const currentSlideCount = currentLink.dataset.cardGallerySlideCount ?? '';
    const incomingSlideCount = incomingLink.dataset.cardGallerySlideCount ?? '';
    if (
      currentSlideCount &&
      incomingSlideCount &&
      currentSlideCount !== incomingSlideCount &&
      !this.#shouldPreserveLiveGalleryShell(currentLink, incomingLink, gallery)
    ) {
      return false;
    }

    const currentActiveSlide = this.#activeMediaSlide();
    const incomingMediaId = this.#incomingActiveMediaId(incomingLink);

    return (
      incomingMediaId !== '' &&
      (currentActiveSlide?.dataset.mediaId === incomingMediaId ||
        this.#shouldPreserveLiveGalleryShell(currentLink, incomingLink, gallery))
    );
  }

  #syncActiveMediaDataFromIncoming(incomingLink) {
    const incomingActiveSlide = this.#incomingActiveMediaSlide(incomingLink);
    if (!(incomingActiveSlide instanceof HTMLElement)) return;

    const incomingMediaId = this.#incomingActiveMediaId(incomingLink);
    let targetSlide = this.#slideByMediaId(incomingMediaId);
    if (!(targetSlide instanceof HTMLElement)) targetSlide = this.#activeMediaSlide();
    if (!(targetSlide instanceof HTMLElement)) return;

    if (targetSlide !== this.#activeMediaSlide()) {
      this.#selectMediaSlide(targetSlide, { commit: true, manual: false });
    }

    for (const name of CARD_GALLERY_SLIDE_STATE_ATTRIBUTES) {
      const value = incomingActiveSlide.getAttribute(name);
      if (value == null) {
        targetSlide.removeAttribute(name);
      } else {
        targetSlide.setAttribute(name, value);
      }
    }
    for (const name of CARD_GALLERY_DATA_ATTRIBUTES) {
      const value = incomingActiveSlide.getAttribute(name);
      if (value == null) {
        targetSlide.removeAttribute(name);
      } else {
        targetSlide.setAttribute(name, value);
      }
    }

    const link = this.#cardGalleryLink;
    if (link instanceof HTMLElement) {
      const mediaId = targetSlide.dataset.mediaId || incomingMediaId;
      link.dataset.activeMediaId = mediaId;
      link.dataset.selectedMediaId = mediaId;
    }
    this.#applyMediaModeToSlide(targetSlide);
    this.#syncGalleryPagination(targetSlide);
  }

  #applyMediaModeToSlide(slide) {
    if (!(slide instanceof HTMLElement)) return false;

    hydrateDeferredMediaSlide(slide);
    const image = slide.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return false;

    promoteIncomingImage(slide);
    const applied = setImageSourceFromDataset(image, slide, this.#mediaMode);
    this.#syncGalleryLinkDataset(slide);
    return applied;
  }

  #syncGalleryLinkDataset(slide) {
    const link = this.#cardGalleryLink;
    if (!(link instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    for (const name of CARD_GALLERY_DATA_ATTRIBUTES) {
      const value = slide.getAttribute(name);
      if (value == null) {
        link.removeAttribute(name);
      } else {
        link.setAttribute(name, value);
      }
    }
  }

  #syncWarmedVariantMedia(optionValueId, pickVersion) {
    if (!optionValueId) return;

    const warmedDocument = this.variantPicker?.getWarmedVariantDocument?.(optionValueId);
    if (!warmedDocument) return;

    const canPatch = () =>
      this.isConnected &&
      this.#variantPickVersion === pickVersion &&
      this.variantPicker?.selectedOptionId === optionValueId;

    void Promise.resolve(warmedDocument)
      .then((html) => {
        if (!html || !canPatch()) return;
        return this.#patchVariantImageFromHtml(html, canPatch);
      })
      .catch(() => {});
  }

  async #syncVariantImages() {
    if (!this.#lastVariantHtml) return;

    const swapVersion = (this.#imageSwapVersion += 1);
    await this.#patchVariantImageFromHtml(
      this.#lastVariantHtml,
      () => this.isConnected && swapVersion === this.#imageSwapVersion,
    );
  }

  async #patchVariantImageFromHtml(sourceHtml, canPatch) {
    const gallery = this.querySelector('[data-ref="cardGallery"]');
    if (!gallery || !sourceHtml) return;

    const currentLink = gallery.querySelector('.card-gallery__media-link');
    const incomingLink = sourceHtml.querySelector('.card-gallery__media-link');
    if (incomingLink && currentLink) {
      const mediaMode = this.#mediaMode;
      if (this.#canPreserveActiveMediaPatch(currentLink, incomingLink, gallery)) {
        const preserveLiveGalleryShell = this.#shouldPreserveLiveGalleryShell(currentLink, incomingLink, gallery);
        if (!canPatch()) return;
        this.#syncActiveMediaDataFromIncoming(incomingLink);
        this.#patchGalleryControlsFromHtml(sourceHtml, gallery, {
          preserveExistingWhenMissing: preserveLiveGalleryShell,
        });
        this.setMediaMode(mediaMode);
        return;
      }

      const incomingPatch = incomingLink.cloneNode(true);
      if (!(incomingPatch instanceof Element)) return;
      promoteIncomingImage(incomingPatch);
      await warmImageCandidate(imageCandidateFrom(incomingPatch), {
        decode: true,
        timeoutMs: PRODUCT_CARD_IMAGE_DECODE_TIMEOUT_MS,
      });
      if (!canPatch()) return;
      patchDOM(currentLink, incomingPatch, { childrenOnly: false });
      this.#patchGalleryControlsFromHtml(sourceHtml, gallery);
      this.setMediaMode(mediaMode);
      return;
    }

    const current = gallery.querySelector('.product-media-container');
    const incoming = sourceHtml.querySelector('.product-media-container');
    if (incoming && current) {
      const incomingPatch = incoming.cloneNode(true);
      if (!(incomingPatch instanceof Element)) return;
      promoteIncomingImage(incomingPatch);
      await warmImageCandidate(imageCandidateFrom(incomingPatch), {
        decode: true,
        timeoutMs: PRODUCT_CARD_IMAGE_DECODE_TIMEOUT_MS,
      });
      if (!canPatch()) return;
      patchDOM(current, incomingPatch);
    }
  }

  #patchGalleryControlsFromHtml(sourceHtml, gallery, { preserveExistingWhenMissing = false } = {}) {
    const mediaLink = gallery.querySelector('.card-gallery__media-link');
    if (!(mediaLink instanceof HTMLElement)) return;

    const incomingControls = Array.from(
      sourceHtml.querySelectorAll('.card-gallery__controls, .card-gallery__pagination'),
    ).filter((node) => node instanceof HTMLElement);
    if (incomingControls.length === 0 && preserveExistingWhenMissing) {
      this.#syncGalleryPagination();
      return;
    }

    for (const currentControl of gallery.querySelectorAll('.card-gallery__controls, .card-gallery__pagination')) {
      currentControl.remove();
    }

    let insertAfter = mediaLink;
    for (const incomingControl of incomingControls) {
      const clone = incomingControl.cloneNode(true);
      if (!(clone instanceof HTMLElement)) continue;
      insertAfter.after(clone);
      insertAfter = clone;
    }

    this.#syncGalleryPagination();
  }

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.defaultPrevented) return;
    if (this.#isInteractiveClickTarget(event.target)) return;

    const link = this.productLink;
    if (!(link instanceof HTMLAnchorElement) || !link.href) return;

    const linkURL = new URL(link.href);
    const anchor = link.getAttribute('id');
    if (!anchor) return;

    const url = new URL(window.location.href);
    url.hash = anchor;

    const page = this.closest('li')?.dataset?.page;
    if (page) url.searchParams.set('page', page);

    if (window.Shopify?.designMode !== true) {
      requestYieldCallback(() => history.replaceState({}, '', url.toString()));
    }

    if (!event.target.closest('a')) this.#goToURL(event, linkURL);
  };

  #isInteractiveClickTarget(target) {
    const interactiveTarget = target.closest(PRODUCT_TILE_INTERACTIVE_SELECTOR);
    return interactiveTarget instanceof Element && this.contains(interactiveTarget);
  }
}

if (!customElements.get('product-tile')) {
  customElements.define('product-tile', ProductItem);
}

class SwatchesVariantPickerComponent extends VariantPicker {
  #prefetchedVariantDocuments = new Map();
  #prefetchedVariantDocumentRequests = new Map();
  #prefetchedVariantUrls = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(this, 'pointerover', this.#onOptionPrewarm, { capture: true, passive: true });
    this.addManagedEventListener(this, 'focusin', this.#onOptionPrewarm, { capture: true });
  }

  prefetchVariantSections() {
    if (window.Shopify?.designMode === true) return;

    const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 120));
    idle(() => {
      for (const url of this.#collectPrefetchUrls()) {
        if (this.#prefetchedVariantUrls.has(url)) continue;
        void this.#prefetchVariantDocument(url);
      }
    });
  }

  getWarmedVariantDocument(optionValueId) {
    const input = this.#findInputByOptionValueId(optionValueId);
    if (!input) return null;

    const url = this.#buildProductCardRequestUrl(input);
    if (!url) return null;

    return this.#prefetchedVariantDocuments.get(url) ?? this.#prefetchedVariantDocumentRequests.get(url) ?? null;
  }

  getOptionMediaId(optionValueId) {
    return this.#findInputByOptionValueId(optionValueId)?.dataset.optionMediaId ?? '';
  }

  #onOptionPrewarm = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const input =
      target instanceof HTMLInputElement
        ? target
        : target?.closest('label')?.querySelector('input[data-option-value-id]');

    if (!(input instanceof HTMLInputElement)) return;
    this.#prewarmOption(input);
  };

  #prewarmOption(input) {
    if (window.Shopify?.designMode === true) return;
    if (input.disabled || input.getAttribute('aria-disabled') === 'true') return;

    const url = this.#buildProductCardRequestUrl(input);

    if (!url || this.#prefetchedVariantUrls.has(url)) return;
    void this.#prefetchVariantDocument(url);
  }

  #collectPrefetchUrls() {
    const urls = [];
    const seen = new Set();

    for (const input of this.querySelectorAll('input[data-option-value-id]')) {
      if (!(input instanceof HTMLInputElement) || input.disabled || input.getAttribute('aria-disabled') === 'true') continue;

      const href = this.#buildProductCardRequestUrl(input);

      if (!href) continue;
      if (seen.has(href)) continue;
      seen.add(href);
      urls.push(href);

      if (urls.length >= 6) break;
    }

    return urls;
  }

  #prefetchVariantDocument(url) {
    if (!url) return Promise.resolve(null);

    const cached = this.#prefetchedVariantDocuments.get(url);
    if (cached) return Promise.resolve(cached);

    const pending = this.#prefetchedVariantDocumentRequests.get(url);
    if (pending) return pending;

    this.#prefetchedVariantUrls.add(url);
    const request = this.prefetchUpdatedSection(url)
      .then((doc) => {
        this.#prefetchedVariantDocumentRequests.delete(url);
        if (!doc) {
          this.#prefetchedVariantUrls.delete(url);
          this.#prefetchedVariantDocuments.delete(url);
          return null;
        }

        this.#prefetchedVariantDocuments.set(url, doc);
        preloadCardImageFromDocument(doc);
        return doc;
      })
      .catch(() => {
        this.#prefetchedVariantDocumentRequests.delete(url);
        this.#prefetchedVariantUrls.delete(url);
        this.#prefetchedVariantDocuments.delete(url);
        return null;
      });

    this.#prefetchedVariantDocumentRequests.set(url, request);
    return request;
  }

  #buildProductCardRequestUrl(input) {
    try {
      return this.buildRequestUrl(input, 'product-card', this.getOptionValueIds(input));
    } catch {
      return '';
    }
  }

  #findInputByOptionValueId(optionValueId) {
    if (!optionValueId) return null;

    return (
      Array.from(this.querySelectorAll('input[data-option-value-id]')).find(
        (input) => input instanceof HTMLInputElement && input.dataset.optionValueId === optionValueId,
      ) ?? null
    );
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const { overflowList } = this.refs;
    if (overflowList instanceof OverflowList) overflowList.showAll();
  }
}

if (!customElements.get('swatch-picker')) {
  customElements.define('swatch-picker', SwatchesVariantPickerComponent);
}
